import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Import the RTK Query methods from the React-specific entry point

// -----------------------------------------------------------------------------
// Importing helpers
// -----------------------------------------------------------------------------
import { getStringFromLocalStorage } from "helpers/localStorage";
import { getApiHost } from "helpers/constants";

const handleFetchBaseQueryError = (error) => {
	if (error?.status === 503) {
		window.location.href = "/error/503";
	}
};

const baseQueryWithInterceptor = async (args, api, extraOptions) => {
	const result = await fetchBaseQuery({
		baseUrl: getApiHost(),
		mode: "cors",
		// Add Auth Headers to
		prepareHeaders: (headers, { getState }) => {
			// By default, if we have a token in the store, let's use that for authenticated requests
			const jwtToken = getStringFromLocalStorage("creator_jwt_token");
			if (jwtToken) {
				headers.set("Authorization", `${jwtToken}`);
			}

			return headers;
		},
	})(args, api, extraOptions);

	if (result.error) {
		handleFetchBaseQueryError(result.error);
	}

	return result;
};
// Define our single API slice object
export const apiSlice = createApi({
	// The cache reducer expects to be added at `state.api` (already default - this is optional)
	reducerPath: "api",
	// All of our requests will have URLs starting with '/fakeApi'
	baseQuery: baseQueryWithInterceptor,

	// The "endpoints" represent operations and requests for this server
	endpoints: (builder) => ({
		// Authentication
		login: builder.mutation({
			query: (params) => ({
				url: `/users/login`,
				method: "POST",
				body: params,
			}),
		}),
		signUp: builder.mutation({
			query: (params) => ({
				url: `/users/creator`,
				method: "POST",
				body: params,
			}),
		}),
		signUpConsumerAtGym: builder.mutation({
			query: (params) => ({
				url: `/users/gym/${params.gym_id}`,
				method: "POST",
				body: params,
			}),
		}),
		forgotPassword: builder.mutation({
			query: (params) => ({
				url: `/users/reset-password`,
				method: "POST",
				body: params,
			}),
		}),
		updatePassword: builder.mutation({
			query: (params) => ({
				url: `/users/update-password`,
				method: "PUT",
				body: params,
			}),
		}),
		confirmResetPassword: builder.mutation({
			query: (params) => ({
				url: `/users/reset-password/confirmation`,
				method: "POST",
				body: params,
			}),
		}),
		// Assets
		getS3SignedUrl: builder.mutation({
			query: (params) => ({
				url: `/workouts/get-signed-url`,
				method: "POST",
				body: { ...params },
			}),
		}),
		getMuxAuthenticatedUrl: builder.mutation({
			query: () => `/mux/get-authenticated-mux-url`,
		}),
		getMuxVideoUrl: builder.query({
			query: ({ playbackId }) => `/mux/get-mux-video-url/${playbackId}`,
		}),
		uploadAssetToS3: builder.mutation({
			query: ({ url, formData }) => ({
				headers: { "Content-Type": "multipart/form-data" },
				url: url,
				method: "POST",
				body: formData,
			}),
		}),
		// See assets Services
		// uploadAssetToS3: builder.query({
		// 	query: ({ url, fields, file: { original } }) => `/branding/${gym_id}`,
		// }),
		// Categories
		getCategories: builder.query({
			query: (gym_id) => `/categories/get-all/${gym_id}`,
		}),
		deleteCategory: builder.mutation({
			query: (params) => ({
				url: `/categories/delete`,
				method: "PUT",
				body: params,
			}),
		}),
		// Domain Gym
		getDomainGym: builder.query({
			query: (hostname) => `/domains/lookup/${hostname}`,
		}),
		// Fit Services
		getGenres: builder.query({
			query: () => `/fit/genres`,
		}),
		// Gym
		createGym: builder.mutation({
			query: (params) => ({
				url: `/gyms`,
				method: "POST",
				body: params,
			}),
		}),
		editGym: builder.mutation({
			query: (params) => ({
				url: `/gyms/edit`,
				method: "PUT",
				body: params,
			}),
		}),
		getGymBranding: builder.query({
			query: (gym_id) => `/branding/${gym_id}`,
		}),
		createGymAsset: builder.mutation({
			query: ({ formData, jwt }) => ({
				url: `/gyms/assets`,
				method: "POST",
				headers: jwt ? { Authorization: `${jwt}` } : null,
				body: formData,
				formData: true,
			}),
		}),
		getGymFromName: builder.query({
			query: (name) => `/gyms/by-name/${name}`,
		}),
		updateGymBrandingColors: builder.mutation({
			query: (params) => ({
				url: `/gyms/branding`,
				method: "PUT",
				body: params,
			}),
		}),
		createGymBrandingColors: builder.mutation({
			query: (params) => ({
				url: `/gyms/branding`,
				method: "POST",
				body: params,
			}),
		}),
		updateGymSubdomain: builder.mutation({
			query: (params) => ({
				url: `/gyms/update-subdomain`,
				method: "PUT",
				body: params,
			}),
		}),
		checkSubdomain: builder.query({
			query: (subdomain) => `/domains/lookup/${subdomain}`,
		}),
		searchGyms: builder.query({
			query: ({ term, zip }) => {
				const params = new URLSearchParams();
				if (term) params.append("terms", term);
				if (zip) params.append("zipcode", zip);
				return `/autocomplete/gyms?${params.toString()}`;
			},
		}),
		getGymMemberList: builder.query({
			query: ({ gym_id }) => `/gyms/users/${gym_id}`,
		}),
		// Leads
		createLead: builder.mutation({
			query: (params) => ({
				url: `leads`,
				method: "POST",
				body: params,
			}),
		}),
		// Members
		getGymMembersByDate: builder.query({
			query: ({ start_date, end_date }) => {
				const params = new URLSearchParams();
				if (start_date) params.append("start_date", start_date);
				if (end_date) params.append("end_date", end_date);
				return `/gyms/members?${params.toString()}`;
			},
		}),
		getGymMonthlyActiveUsers: builder.query({
			query: () => "/gyms/monthly-active-users",
		}),
		// Payments
		getProducts: builder.query({
			query: () => `/payments/v2/creator/products`,
		}),
		createConnectedAccount: builder.mutation({
			query: (params) => ({
				url: `/payments/create-connected-account`,
				method: "POST",
				body: params,
			}),
		}),
		createCustomer: builder.mutation({
			query: (params) => ({
				url: `/payments/create-customer`,
				method: "POST",
				headers: params.auth_token ? { Authorization: `${params.auth_token}` } : undefined,
				body: params,
			}),
		}),
		updateUserCustomerId: builder.mutation({
			query: (params) => ({
				url: `/payments/set-user-customer-id`,
				method: "POST",
				headers: params.auth_token ? { Authorization: `${params.auth_token}` } : null,
				body: params.body,
			}),
		}),
		cancelSubscription: builder.mutation({
			query: (params) => ({
				url: `/payments/cancel-subscription`,
				method: "POST",
				body: params,
			}),
		}),
		// Users
		deleteUser: builder.mutation({
			query: (params) => ({
				url: `/users/delete`,
				method: "DELETE",
				body: { data: params },
			}),
		}),
		getCurrentUser: builder.query({
			query: ({ user_id }) => `/users/${user_id}`,
		}),
		updateUserAssociation: builder.mutation({
			query: (params) => ({
				url: `/users/associations`,
				headers: params.auth_token ? { Authorization: `${params.auth_token}` } : undefined,
				method: "POST",
				body: params,
			}),
		}),
		updateUserProfile: builder.mutation({
			query: (params) => ({
				url: `/users/edit`,
				method: "PUT",
				headers: params.auth_token ? { Authorization: `${params.auth_token}` } : undefined,
				body: params,
			}),
		}),
		// Pages
		createPage: builder.mutation({
			query: (params) => ({
				url: `/pages`,
				method: "POST",
				body: params,
			}),
		}),
		editPage: builder.mutation({
			query: ({ params }) => ({
				url: `/pages`,
				method: "PUT",
				body: params,
			}),
		}),
		createPageAsset: builder.mutation({
			query: ({ formData, jwt }) => ({
				url: `/pages/assets`,
				method: "POST",
				headers: jwt ? { Authorization: `${jwt}` } : undefined,
				body: formData,
				formData: true,
			}),
		}),
		getPages: builder.query({
			query: (gym_id) => `/pages/${gym_id}`,
		}),
		// Workouts
		getGymWorkouts: builder.query({
			query: ({ gym_id, start_date, end_date }) => {
				const params = new URLSearchParams();

				if (start_date) {
					params.append("start_date", start_date);
				}

				if (end_date) {
					params.append("end_date", end_date);
				}

				return `/workouts/${gym_id}?${params.toString()}`;
			},
		}),
		getScheduledStreams: builder.query({
			query: (gym_id) => `/workouts/scheduled-streams/${gym_id}`,
		}),
		createWorkout: builder.mutation({
			query: (params) => ({
				url: `/workouts`,
				method: "POST",
				body: params,
			}),
		}),
		editWorkout: builder.mutation({
			query: (params) => ({
				url: `/workouts/${params.workout_id}`,
				method: "PUT",
				body: params,
			}),
		}),
		deleteWorkout: builder.mutation({
			query: (params) => ({
				url: `/workouts/`,
				method: "DELETE",
				body: params,
			}),
		}),
		scheduleWorkout: builder.mutation({
			query: (params) => ({
				url: `/workout/schedule`,
				method: "POST",
				body: params,
			}),
		}),
		editScheduledStream: builder.mutation({
			query: (params) => ({
				url: `/workout/schedule`,
				method: "PUT",
				body: params,
			}),
		}),
		getWorkoutNextMix: builder.query({
			query: (gym_id) => `/workout/music/next/${gym_id}`,
		}),
		setStreamKeys: builder.mutation({
			query: (params) => ({
				url: `/workouts/stream-keys`,
				method: "POST",
				body: params,
			}),
		}),
		getStreamKeys: builder.query({
			query: (gym_id) => `/workouts/stream-keys/${gym_id}`,
		}),
		// STATS
		getStatsForGym: builder.query({
			query: ({ gym_id, ...params }) => ({
				url: `/stats/gym/${gym_id}`,
				method: "GET",
				params: params,
			}),
		}),
		// /stats/song-play - POST { mix_id: number, track_id: number, workout_id: string, gym_id: string, isrc: string, source_detail: string, source_stream: string, end_reason: number, play_length: number }
		sendSongPlayStats: builder.mutation({
			query: (params) => ({
				url: "/stats/song-play",
				method: "POST",
				body: params,
			}),
		}),
		// /users/send-otp-to-phone-number - POST { new_phone_number: string }
		sendOtpToPhoneNumber: builder.mutation({
			query: ({ token, ...params }) => ({
				url: "/users/send-otp-to-phone-number",
				method: "POST",
				headers: token ? { Authorization: `${token}` } : undefined,
				body: params,
			}),
		}),
		// /users/verify-otp-and-change-phone-number - POST { phone_number: string, code: string }
		verifyOtpAndChangePhoneNumber: builder.mutation({
			query: ({ token, ...params }) => ({
				url: "/users/verify-otp-and-change-phone-number",
				headers: token ? { Authorization: `${token}` } : undefined,
				method: "POST",
				body: params,
			}),
		}),
		// /users/send-email-change-link - POST { new_email_address: string, domain }
		sendEmailChangeLink: builder.mutation({
			query: (params) => ({
				url: "/users/send-email-change-link",
				method: "POST",
				body: params,
			}),
		}),
		// /users/confirm-email-change-link - POST { token: string, user_id: string }
		confirmEmailChangeLink: builder.mutation({
			query: (params) => ({
				url: "/users/confirm-email-change-link",
				method: "POST",
				body: params,
			}),
		}),

		// Stripe
		stripeCheckSubscriptionStatus: builder.mutation({
			query: (params) => ({
				url: "/payments/check-subscription-status",
				method: "POST",
				body: params,
			}),
		}),
		stripeUpdateMembershipPricing: builder.mutation({
			query: (params) => ({
				url: `/payments/update-membership-pricing`,
				method: "POST",
				body: params,
			}),
		}),
		stripeCreateSubscriptionPrice: builder.mutation({
			query: (params) => ({
				url: `/payments/create-subscription-price`,
				method: "POST",
				body: params,
			}),
		}),
		stripeGetGymSubscription: builder.query({
			query: (params) => ({ url: `/payments/get-gym-subscription/${params.gymId}`, method: "GET" }),
		}),
		stripeFetchTransfers: builder.mutation({
			query: (params) => ({
				url: "/payments/fetch-transfers",
				method: "POST",
				body: params,
			}),
		}),
		stripeGetCustomerConnectedExternalAccounts: builder.mutation({
			query: (params) => ({
				url: "/payments/get-customer-connected-external-accounts",
				method: "POST",
				body: params,
			}),
		}),
		stripeCreateOnboardingLink: builder.mutation({
			query: (params) => ({
				url: "/payments/create-onboarding-link",
				method: "POST",
				body: params,
			}),
		}),
		stripeGetPaymentMethods: builder.mutation({
			query: (params) => ({
				url: "/payments/get-payment-methods",
				method: "POST",
				body: params,
			}),
		}),
		getPaymentMethods: builder.query({
			query: (params) => ({
				url: "/payments/get-payment-methods",
				method: "POST",
				body: params,
			}),
		}),
		stripeDetachPaymentMethod: builder.mutation({
			query: (params) => ({
				url: "/payments/detach-payment-method",
				method: "POST",
				body: params,
			}),
		}),
		stripeGetGymProduct: builder.query({
			query: (params) => ({
				url: `/payments/get-gym-product/${params.gymId}`,
				method: "GET",
			}),
		}),
		stripeGetGymProductInternal: builder.query({
			query: (params) => ({
				url: `/payments/get-gym-product-internal/${params.gymId}`,
				method: "GET",
			}),
		}),
		stripeCreateMemberCheckoutSession: builder.mutation({
			query: (params) => ({
				url: "/payments/create-member-checkout-session",
				method: "POST",
				body: params,
			}),
		}),
		stripeCreateGymCheckoutSession: builder.mutation({
			query: (params) => ({
				url: "/payments/create-gym-checkout-session",
				method: "POST",
				body: params,
			}),
		}),
		stripeUpdateGymSubscription: builder.mutation({
			query: (params) => ({
				url: "/payments/update-gym-subscription-plan",
				method: "POST",
				body: params,
			}),
		}),
		stripeCreateSetupSession: builder.mutation({
			query: (params) => ({
				url: "/payments/create-setup-session",
				method: "POST",
				body: params,
			}),
		}),

		// ux
		muxCreateMuxLiveStream: builder.mutation({
			query: (params) => ({
				url: "/mux/create-live-stream",
				method: "POST",
				body: params,
			}),
		}),
		// /users/check-reset-password-token POST { token: string }
		checkResetPasswordToken: builder.mutation({
			query: (params) => ({
				url: "/users/check-reset-password-token",
				method: "POST",
				body: params,
			}),
		}),
		// POST /check-twofa { code: string, gym_id: string, user_id: string }
		checkTwoFactorAuthentication: builder.mutation({
			query: (params) => ({
				url: "/check-twofa",
				method: "POST",
				body: params,
			}),
		}),
		// POST /users/send-otp - POST { user_id: string }
		sendOtp: builder.mutation({
			query: (params) => ({
				url: "/users/send-otp",
				method: "POST",
				body: params,
			}),
		}),
		// POST /users/verify-otp - POST { user_id: string, code: string }
		verifyOtp: builder.mutation({
			query: (params) => ({
				url: "/users/verify-otp",
				method: "POST",
				body: params,
			}),
		}),
		// GET /payments/check-customer-pm/{gym_id}/{user_id}
		checkCustomerPaymentMethod: builder.query({
			query: ({ gymId, userId }) => ({
				url: `/payments/check-customer-pm/${gymId}/${userId}`,
				method: "GET",
			}),
		}),
		// GET /workouts/paginated/{gym_id}
		getPaginatedWorkouts: builder.query({
			query: ({ gymId, ...qParams }) => {
				const params = new URLSearchParams();
				// ?page=1&page_size=5&order_by_field=name&order_by_direction=desc&search_query=hj&categories=1&categories=3&types=scheduled_on_demand&statuses=active&start_date=2025-01-01&end_date=2025-12-31
				if (qParams.page) params.append("page", qParams.page);
				if (qParams.pageSize) params.append("page_size", qParams.pageSize);
				if (qParams.orderBy) params.append("order_by_field", qParams.orderBy);
				if (qParams.order) params.append("order_by_direction", qParams.order);
				if (qParams.search) params.append("search_query", qParams.search);
				if (qParams.categories) {
					if (Array.isArray(qParams.categories)) {
						qParams.categories.forEach((category) => params.append("categories", category));
					} else {
						params.append("categories", qParams.categories);
					}
				}
				if (qParams.types) {
					if (Array.isArray(qParams.types)) {
						qParams.types.forEach((type) => params.append("types", type));
					} else {
						params.append("types", qParams.types);
					}
				}
				if (qParams.statuses) {
					if (Array.isArray(qParams.statuses)) {
						qParams.statuses.forEach((status) => params.append("statuses", status));
					} else {
						params.append("statuses", qParams.statuses);
					}
				}
				if (qParams.startDate) params.append("start_date", qParams.startDate);
				if (qParams.endDate) params.append("end_date", qParams.endDate);
				return `/workouts/paginated/${gymId}?${params.toString()}`;
			},
		}),
		unscheduleSubscriptionCancellation: builder.mutation({
			query: (params) => ({
				url: `/payments/unschedule-subscription-cancellation`,
				method: "POST",
				body: params,
			}),
		}),

		createCoach: builder.mutation({
			query: (formData) => ({
				url: `/coaches`,
				method: "POST",
				body: formData,
				formData: true,
			}),
		}),

		updateCoach: builder.mutation({
			query: ({ body, coachId }) => ({
				url: `/coaches/${coachId}`,
				method: "PUT",
				body,
				formData: true,
			}),
		}),
		// https://api-dev.sweatbase.com/coaches/gym/:gym_id
		getCoaches: builder.query({
			query: (gymId) => `/coaches/gym/${gymId}`,
		}),
		// DELETE /coaches/coach_id
		deleteCoach: builder.mutation({
			query: (coachId) => ({
				url: `/coaches/${coachId}`,
				method: "DELETE",
			}),
		}),
		// PATCH /workout/{workout_id}/schedule-pre-recorded { start_time, time_zone}
		scheduleUpdatePreRecordedWorkout: builder.mutation({
			query: ({ workoutId, ...body }) => ({
				url: `/workout/${workoutId}/schedule-pre-recorded`,
				method: "PATCH",
				body,
			}),
		}),
		// /equipment/gym/{gym_id}
		getGymEquipment: builder.query({
			query: (gym_id) => ({
				url: `/equipment/gym/${gym_id}`,
				method: "GET",
			}),
		}),
		// /equipment
		addGymEquipment: builder.mutation({
			query: (body) => ({
				url: `/equipment`,
				method: "POST",
				body,
				formData: true,
			}),
		}),
		// PUT /equipment
		editGymEquipment: builder.mutation({
			query: ({ id, ...body }) => ({
				url: `/equipment/${id}`,
				method: "PUT",
				body,
				formData: true,
			}),
		}),
		// DELETE /equipment/:equipment_id
		deleteGymEquipment: builder.mutation({
			query: (equipmentId) => ({
				url: `/equipment/${equipmentId}`,
				method: "DELETE",
			}),
		}),
		// /users/terms-of-use
		checkTermsOfUse: builder.query({
			query: () => ({
				url: `/users/terms-of-use`,
				method: "GET",
			}),
		}),
		// POST /users/terms-of-use
		acceptTermsOfUse: builder.mutation({
			query: (params) => ({
				url: `/users/terms-of-use`,
				method: "POST",
				body: params,
			}),
		}),
	}),
});

// Export the auto-generated hooks
export const {
	//Authentication
	useLoginMutation,
	useSignUpConsumerAtGymMutation,
	useSignUpMutation,
	useForgotPasswordMutation,
	useConfirmResetPasswordMutation,
	useUpdatePasswordMutation,
	// Assets
	useGetS3SignedUrlMutation,
	useGetMuxAuthenticatedUrlMutation,
	useGetMuxVideoUrlQuery,
	useLazyGetMuxVideoUrlQuery,
	useUploadAssetToS3Mutation,
	// Categories
	useGetCategoriesQuery,
	useDeleteCategoryMutation,
	// Domain Gym
	useGetDomainGymQuery,
	// Fit Services
	useGetGenresQuery,
	// Gym
	useCreateGymMutation,
	useEditGymMutation,
	useGetGymBrandingQuery,
	useCreateGymAssetMutation,
	useUpdateGymBrandingColorsMutation,
	useCreateGymBrandingColorsMutation,
	useUpdateGymSubdomainMutation,
	useLazyCheckSubdomainQuery,
	useSearchGymsQuery,
	useLazySearchGymsQuery,
	useGetGymMemberListQuery,
	useGetGymFromNameQuery,
	useLazyGetGymFromNameQuery,
	// Leads
	useCreateLeadMutation,
	// Members
	useGetGymMembersByDateQuery,
	useGetGymMonthlyActiveUsersQuery,
	// Payments
	useUpdateUserCustomerIdMutation,
	useGetProductsQuery,
	useCreateCustomerMutation,
	useCreateConnectedAccountMutation,
	useCancelSubscriptionMutation,
	// Users
	useDeleteUserMutation,
	useUpdateUserAssociationMutation,
	useGetCurrentUserQuery,
	useUpdateUserProfileMutation,
	useSendEmailChangeLinkMutation,
	useConfirmEmailChangeLinkMutation,
	useSendOtpToPhoneNumberMutation,
	useVerifyOtpAndChangePhoneNumberMutation,
	// Pages
	useCreatePageMutation,
	useLazyGetPagesQuery,
	useGetPagesQuery,
	useEditPageMutation,
	useCreatePageAssetMutation,
	// Workouts
	useGetGymWorkoutsQuery,
	useCreateWorkoutMutation,
	useEditWorkoutMutation,
	useDeleteWorkoutMutation,
	useScheduleWorkoutMutation,
	useEditScheduledStreamMutation,
	useLazyGetWorkoutNextMixQuery,
	useGetScheduledStreamsQuery,
	useSetStreamKeysMutation,
	useGetStreamKeysQuery,
	useLazyGetPaginatedWorkoutsQuery,
	// Stats
	useGetStatsForGymQuery,
	useSendSongPlayStatsMutation,
	// Stripe
	useStripeCheckSubscriptionStatusMutation,
	useStripeUpdateMembershipPricingMutation,
	useStripeCreateSubscriptionPriceMutation,
	useStripeGetGymSubscriptionQuery,
	useStripeFetchTransfersMutation,
	useStripeGetCustomerConnectedExternalAccountsMutation,
	useStripeCreateOnboardingLinkMutation,
	useStripeGetPaymentMethodsMutation,
	useGetPaymentMethodsQuery,
	useStripeGetGymProductQuery,
	useStripeGetGymProductInternalQuery,
	useStripeCreateMemberCheckoutSessionMutation,
	useStripeCreateGymCheckoutSessionMutation,
	useStripeUpdateGymSubscriptionMutation,
	useStripeCreateSetupSessionMutation,
	useMuxCreateMuxLiveStreamMutation,
	useStripeDetachPaymentMethodMutation,
	useCheckResetPasswordTokenMutation,
	useCheckTwoFactorAuthenticationMutation,
	useSendOtpMutation,
	useVerifyOtpMutation,
	useCheckCustomerPaymentMethodQuery,
	useUnscheduleSubscriptionCancellationMutation,

	// Coaches
	useCreateCoachMutation,
	useUpdateCoachMutation,
	useGetCoachesQuery,
	useDeleteCoachMutation,
	useScheduleUpdatePreRecordedWorkoutMutation,
	// Equipment
	useGetGymEquipmentQuery,
	useAddGymEquipmentMutation,
	useEditGymEquipmentMutation,
	useDeleteGymEquipmentMutation,
	useAcceptTermsOfUseMutation,
	useCheckTermsOfUseQuery,
} = apiSlice;

